import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Input,
  Chip,
  Pagination,
  Tabs,
  Tab,
  Divider,
  Spinner,
  Select,
  SelectItem,
  useDisclosure,
} from "@nextui-org/react";
import { FiPlus, FiSearch } from "react-icons/fi";
import { RiDeleteBin6Line } from "react-icons/ri";
import { Link } from "react-router-dom";
import { columns } from "../data/enquiriesData";
import { changeStatus, getMake, getMode } from "../store/slices/customerSlice";
import { useDispatch, useSelector } from "react-redux";
import { STATUS } from "../store/constants";
import { displayDate, formatTime } from "../utils";
import { BiHide } from "react-icons/bi";
import axios from "axios";
import ConfirmationModal from "./ConfirmationModal";
import { notification } from "../store/slices/notificationSlice";

import { toast } from "react-toastify";
import { PiFlagPennantFill } from "react-icons/pi";
/* -------------------------------------------------- */
/*  CONSTANTS                                         */
/* -------------------------------------------------- */
const statusColorMap = {
  new: "primary",
  "in progress": "warning",
  "awaiting approval": "warning",
  accepted: "success",
  dispute: "danger",
  rejected: "danger",
  discarded: "danger",
  cancelled: "danger",
};
const INITIAL_VISIBLE_COLUMNS = [
  "status",
  "car",
  "industry",
  "part",
  "duration",
  "delete",
];
/* -------------------------------------------------- */
/*  COMPONENT                                         */
/* -------------------------------------------------- */
const Enquiries = () => {
  const dispatch = useDispatch();
  const role = localStorage.getItem("role"); // "CUSTOMER" / "SUPPLIER"
  const token = localStorage.getItem("accessToken");
  const { status, makes, model: modelOptions } = useSelector((s) => s.customer);

  const notifications = useSelector(
    (state) => state.notification.notifications
  );
  /* ---------------------------------- state --------------------------------- */
  const [visibleColumns, setVisibleColumns] = useState(() => {
    let columns = [...INITIAL_VISIBLE_COLUMNS];
    if (role === "CUSTOMER") {
      // remove the "delete" column entirely for CUSTOMERS
      columns = columns.filter((col) => col !== "delete");
    }
    return new Set(columns);
  });
  const [filterValue, setFilterValue] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [requests, setRequests] = useState([]);
  /* supplier‑side filters */
  const [selectedMakes, setSelectedMakes] = useState([]); // numbers
  const [selectedModels, setSelectedModels] = useState([]); // numbers
  /* ui */
  const [rowsPerPage, setRowsPerPage] = useState(6);
  const [page, setPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const confirmModal = useDisclosure();
  /* ---------------- helper: fetch models list ---------------- */
  const fetchModelsForMake = useCallback(
    (idsString) => {
      if (idsString) dispatch(getMode(idsString));
    },
    [dispatch]
  );
  /* ---------------- helper: fetch requests ------------------- */
  const fetchData = useCallback(async () => {
    try {
      dispatch(changeStatus(STATUS.LOADING));
      const qp = new URLSearchParams();
      qp.append("page", 1);
      qp.append("pageSize", 1000);
      if (selectedMakes.length) qp.append("make", selectedMakes.join(","));
      if (selectedModels.length) qp.append("model", selectedModels.join(","));
      if (statusFilter !== "all") qp.append("status", statusFilter);
      const { data } = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/requests/?${qp}`,
        { headers: { authorization: `Bearer ${token}` } }
      );
      const rows = data.details?.rows ?? [];
      rows.sort(
        (a, b) => new Date(b.date_requested) - new Date(a.date_requested)
      );
      setRequests(rows);
      setTotalRecords(data.details?.index?.totalRecords || rows.length);
      dispatch(changeStatus(STATUS.IDLE));
    } catch (err) {
      console.error(err);
      dispatch(changeStatus(STATUS.ERROR));
    }
  }, [dispatch, token, selectedMakes, selectedModels, statusFilter]);
  /* --------------- initial + filters change  ----------------- */
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  /* ---------------- load makes for supplier ------------------ */

  useEffect(() => {
    console.log("Redux notifications data:", notifications);
  }, [notifications]);

  useEffect(() => {
    if (role === "SUPPLIER") dispatch(getMake());
  }, [role, dispatch]);
  /* ---------------------- filter handlers -------------------- */
  const handleMakeChange = (keys) => {
    const list = [...keys].map(Number);
    setSelectedMakes(list);
    setSelectedModels([]); // clear model filter whenever make changes
    fetchModelsForMake(list.join(","));
    setPage(1);
  };
  const handleModelChange = (keys) => {
    const list = [...keys].map(Number);
    setSelectedModels(list);
    setPage(1);
  };
  const clearSupplierFilters = () => {
    setSelectedMakes([]);
    setSelectedModels([]);
    setFilterValue("");
    setStatusFilter("all");
    confirmModal.onClose();
  };
  /* -------------------- client filters ----------------------- */
  const filteredItems = useMemo(() => {
    let data = [...requests];
    if (filterValue.trim()) {
      const q = filterValue.toLowerCase();
      data = data.filter(
        (d) =>
          d.VehicleMake?.name?.toLowerCase().includes(q) ||
          d.VehicleModel?.name?.toLowerCase().includes(q) ||
          d.Part?.name?.toLowerCase().includes(q) ||
          d.User?.first_name?.toLowerCase().includes(q) ||
          d.User?.last_name?.toLowerCase().includes(q)
      );
    }
    if (statusFilter !== "all") {
      data = data.filter((d) =>
        statusFilter.includes(d.RequestStatus.status.toLowerCase())
      );
    }
    return data;
  }, [requests, filterValue, statusFilter]);
  const pages = Math.max(1, Math.ceil(filteredItems.length / rowsPerPage));
  const pagedItems = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return filteredItems.slice(start, start + rowsPerPage);
  }, [filteredItems, page, rowsPerPage]);
  /* ------------------------ table utils ---------------------- */
  const headerColumns = useMemo(
    () => columns.filter((c) => visibleColumns.has(c.uid)),
    [visibleColumns]
  );

  const handleHide = async (id) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/quotes/hiderequest/${id}`,
        {},
        { headers: { authorization: `Bearer ${token}` } }
      );

      toast.success("Request hidden");

      fetchData();
    } catch (error) {
      console.error("Error hiding request:", error);

      toast.error("Failed to hide request");
    }
  };

  const renderCell = useCallback(
    (item, columnKey) => {
      const hasSupplierComment = notifications.some(
        (n) =>
          n.group === "COMMENT" &&
          n.message?.toLowerCase().includes("from supplier") &&
          n.related_id === item.id
      );

      switch (columnKey) {
        case "status":
          return (
            <div className="flex items-center gap-2">
              <Chip
                variant="flat"
                className="capitalize"
                color={statusColorMap[item.RequestStatus.status.toLowerCase()]}
              >
                {item.RequestStatus.status === "In progress"
                  ? "Awaiting Approval"
                  : item.RequestStatus.status}
              </Chip>

              {hasSupplierComment && (
                <PiFlagPennantFill
                  className="text-blue-400 text-sm"
                  title="New Supplier Comment"
                />
              )}
            </div>
          );

        case "industry":
          return (
            role === "SUPPLIER" && (
              <p className="table_cell">
                {item.User?.Industry || "Not Provided"}
              </p>
            )
          );
        case "car":
          return (
            <div className="flex flex-col gap-1">
              <p className="table_cell capitalize">
                {item.VehicleMake?.name?.toLowerCase() ||
                  item.custom_make?.toLowerCase() ||
                  "Not Provided"}{" "}
                {item.VehicleModel ? item.VehicleModel.name : item.custom_model}
              </p>
              <p className="table_cell_sm">Year: {item.year}</p>
            </div>
          );
        case "part":
          return (
            <p className="table_cell capitalize">
              {item.Part?.name?.toLowerCase() ||
                item.custom_part_type ||
                "Not Provided"}
            </p>
          );
        case "duration":
          return (
            <div className="flex flex-col gap-1">
              <p className="table_cell capitalize">
                {formatTime(item.date_requested)}
              </p>
              <p className="table_cell_sm">
                {displayDate(item.date_requested)}
              </p>
            </div>
          );
        case "delete": {
          const role = localStorage.getItem("role");
          if (role !== "SUPPLIER") {
            return null;
          }
          return (
            <div className="flex items-center gap-2 text-lg">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleHide(item.id);
                }}
              >
                <BiHide className="text-blue-500 cursor-pointer" />
              </button>
            </div>
          );
        }
        default:
          return item[columnKey];
      }
    },
    [role,notifications]
  );
  /* -------------------------------------------------- */
  /*  JSX                                               */
  /* -------------------------------------------------- */
  return (
    <>
      <Table
        aria-label="Requests Table"
        isHeaderSticky
        isStriped
        topContent={
          <div className="flex flex-col gap-6">
            {/* search / filter row */}
            <div className="flex justify-between gap-3 items-end w-full">
              {role === "CUSTOMER" ? (
                <Input
                  isClearable
                  className="w-full sm:max-w-[44%]"
                  placeholder="Search Requests..."
                  startContent={<FiSearch />}
                  value={filterValue}
                  onClear={() => setFilterValue("")}
                  onValueChange={(val) => {
                    setFilterValue(val);
                    setPage(1);
                  }}
                />
              ) : (
                <div className="md:grid grid-cols-3 gap-1 capitalize">
                  {/* make */}
                  <Select
                    labelPlacement="outside"
                    variant="bordered"
                    selectionMode="single"
                    className="w-full sm:w-[220px]"
                    placeholder="Select Make"
                    label="Make"
                    selectedKeys={new Set(selectedMakes.map(String))}
                    onSelectionChange={handleMakeChange}
                  >
                    {makes.map(
                      (m) =>
                        m.id !== 0 && (
                          <SelectItem key={String(m.id)} className="capitalize">
                            {m.name.toLowerCase()}
                          </SelectItem>
                        )
                    )}
                  </Select>
                  {/* model */}
                  <Select
                    labelPlacement="outside"
                    variant="bordered"
                    selectionMode="single"
                    className="w-full sm:w-[220px] capitalize"
                    placeholder="Select Model"
                    label="Model"
                    disabledKeys={modelOptions.length ? [] : ["null"]}
                    selectedKeys={new Set(selectedModels.map(String))}
                    onSelectionChange={handleModelChange}
                  >
                    {modelOptions.map(
                      (m) =>
                        m.id !== 0 && (
                          <SelectItem key={String(m.id)}>
                            {m.name.toLowerCase()}
                          </SelectItem>
                        )
                    )}
                  </Select>
                  {/* clear */}
                  <div className="flex justify-center items-center h-[95px]">
                    <button
                      className="bg-red-400 text-white font-semibold px-6 py-2 rounded-md hover:bg-red-700"
                      onClick={confirmModal.onOpen}
                    >
                      Clear All Selections
                    </button>
                  </div>
                </div>
              )}
              {role === "CUSTOMER" && (
                <Button
                  endContent={
                    <FiPlus
                      color="white"
                      className="text-[0.65rem] sm:text-lg"
                    />
                  }
                  className="bg-lightBlue text-white text-[0.65rem] sm:text-base"
                >
                  <Link to="/requests/create">Create Request</Link>
                </Button>
              )}
            </div>
            {/* tabs + rows selector */}
            <div className="flex flex-col-reverse sm:flex-row flex-wrap items-center justify-between gap-y-4">
              <Tabs
                variant="underlined"
                selectedKey={statusFilter}
                onSelectionChange={(key) => {
                  setStatusFilter(key);
                  setPage(1);
                }}
              >
                <Tab key="all" title="All Requests" />
                <Tab key="new" title="New Requests" />
                <Tab key="in progress" title="Awaiting Approval" />
              </Tabs>
              <div className="flex items-center gap-5 text-xs sm:text-sm text-default-400 capitalize">
                {/* {notifications.some(
                  (item) =>
                    item.group === "COMMENT" &&
                    item.message?.startsWith(
                      "You have a new comment from supplier"
                    )
                ) ? (
                  <p>comments</p>
                ) : (
                  <></>
                )} */}

                {/* {notifications
                  .filter((item) => item.group === "COMMENT")
                  .map((item) => (
                    <div key={item.id} className="mb-2 p-2 border rounded">
                      <p className="text-sm text-gray-700 font-semibold">
                        Comment ID: {item.related_id}
                      </p>
                    </div>
                  ))} */}
                <span>Total: {totalRecords} Requests</span>
                <Divider orientation="vertical" className="h-6" />
                <label className="flex items-center">
                  Rows per page:&nbsp;
                  <select
                    className="bg-transparent outline-none"
                    value={rowsPerPage}
                    onChange={(e) => {
                      setRowsPerPage(Number(e.target.value));
                      setPage(1);
                    }}
                  >
                    <option value="6">6</option>
                    <option value="10">10</option>
                    <option value="15">15</option>
                  </select>
                </label>
              </div>
            </div>
          </div>
        }
        topContentPlacement="outside"
        bottomContent={
          <div className="py-2 flex justify-center">
            <Pagination
              isCompact
              showControls
              color="primary"
              page={page}
              total={pages}
              onChange={setPage}
            />
          </div>
        }
        bottomContentPlacement="outside"
        classNames={{ td: "whitespace-nowrap" }}
      >
        {/* header */}
        <TableHeader columns={headerColumns}>
          {(col) => (
            <TableColumn
              key={col.uid}
              allowsSorting={col.sortable}
              className="capitalize"
            >
              {role === "CUSTOMER" && col.name === "Industry" ? "" : col.name}
            </TableColumn>
          )}
        </TableHeader>
        {/* body */}
        <TableBody
          items={pagedItems}
          emptyContent="No Requests Found"
          isLoading={status === STATUS.LOADING && !requests.length}
          loadingContent={<Spinner label="Loading..." />}
        >
          {(item) => (
            <TableRow key={item.id}>
              {(colKey) => (
                <TableCell>
                  {colKey === "icon" ? (
                    <Button size="sm" color="error">
                      <RiDeleteBin6Line className="text-xl text-red-500" />
                    </Button>
                  ) : role === "SUPPLIER" ? (
                    // <Link to={`/requestDetails/${item.id}`}>
                    //   {renderCell(item, colKey)}
                    // </Link>
                    <a
                      href={`/requestDetails/${item.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {renderCell(item, colKey)}
                    </a>
                  ) : (
                    // <Link to={`/requests/${item.id}`}>
                    //   {renderCell(item, colKey)}
                    // </Link>
                    <a
                      href={`/requests/${item.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {renderCell(item, colKey)}
                    </a>
                  )}
                </TableCell>
              )}
            </TableRow>
          )}
        </TableBody>
      </Table>
      {/* confirm modal */}
      {confirmModal.isOpen && (
        <ConfirmationModal
          isOpen={confirmModal.isOpen}
          onClose={confirmModal.onClose}
          onConfirm={clearSupplierFilters}
        />
      )}
    </>
  );
};
export default Enquiries;
